/* Ask Co-Pilot — bidirectional chat in the right rail of Agent Desk.
 *
 * Sends the agent's question + recent history to /api/agent-desk/chat;
 * the worker proxies to the bot which calls OpenAI with one tool
 * (list_customer_calls). Foundation only — no streaming yet, no rich
 * payloads, no quick-chip overrides. Just text bubbles. */

import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { API_URL } from "../lib/api";

type Role = "agent" | "copilot" | "system";

type Message = {
  id: string;
  role: Role;
  content: string;
};

// Minimal inline markdown for co-pilot bubbles: **bold** and `code`.
// The bot returns lightly-formatted text (numbered lists, bolded
// headings) and the bubble already preserves newlines via pre-wrap, so
// we don't need a full markdown engine.
function renderMarkdown(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={key++}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      parts.push(<code key={key++}>{m[2]}</code>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) =>
    typeof p === "string" ? <Fragment key={i}>{p}</Fragment> : p,
  );
}

const TOOL_LABEL: Record<string, string> = {
  list_customer_calls:         "Looking up call history",
  list_customer_offer_history: "Looking up past offers",
  last_3_bills:                "Pulling recent bills",
  competitor_catalog:          "Looking up competitor offers",
};

function toolLabel(name: string): string {
  return TOOL_LABEL[name] ?? `Calling ${name}`;
}

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function AskCoPilot({
  callId,
  customerId,
}: {
  callId: number | undefined;
  customerId: number | undefined;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Sticky call/customer — once we've seen a call, hold onto the id even
  // after useCurrentCall flips back to idle (wrap-up window expires or
  // the agent hits Done). The chat then keeps working post-call so the
  // agent can follow up on offers, history, etc. Only a *new* call
  // (different id) tears down the thread.
  const [stickyCallId, setStickyCallId] = useState<number | undefined>(callId);
  const [stickyCustomerId, setStickyCustomerId] =
    useState<number | undefined>(customerId);

  useEffect(() => {
    if (callId !== undefined) setStickyCallId(callId);
  }, [callId]);

  useEffect(() => {
    if (customerId !== undefined) setStickyCustomerId(customerId);
  }, [customerId]);

  useEffect(() => {
    setMessages([]);
    setDraft("");
    setErr(null);
    setActiveTool(null);
  }, [stickyCallId, stickyCustomerId]);

  // Auto-scroll the body to the latest message after each render.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  async function send(e: FormEvent) {
    e.preventDefault();
    const q = draft.trim();
    if (!q || busy) return;

    const agentMsg: Message = { id: nextId(), role: "agent", content: q };
    const next = [...messages, agentMsg];
    setMessages(next);
    setDraft("");
    setBusy(true);
    setErr(null);

    // Open a placeholder copilot bubble that we'll grow as deltas arrive.
    const replyId = nextId();
    setMessages((cur) => [...cur, { id: replyId, role: "copilot", content: "" }]);
    setActiveTool(null);

    try {
      const r = await fetch(`${API_URL}/api/agent-desk/chat`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          message: q,
          customer_id: stickyCustomerId ?? null,
          call_id: stickyCallId ?? null,
          // Prior turns only — `message` carries the new turn. Sending
          // `next` (which includes agentMsg) here would duplicate the
          // user turn server-side once the bot appends req.message after
          // iterating req.history. See docs/rfc-ask-copilot-messages.md
          // for the long-term schema collapse that removes this split.
          history: messages
            .filter((m) => m.role !== "system")
            .map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      if (!r.ok || !r.body) {
        setErr(`chat failed (${r.status})`);
        // Drop the empty placeholder so the user doesn't see a blank bubble.
        setMessages((cur) => cur.filter((m) => m.id !== replyId));
        return;
      }

      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamErr: string | null = null;

      // Parse the SSE chunks as they land. Each event is `data: {…}\n\n`.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep = buffer.indexOf("\n\n");
        while (sep !== -1) {
          const block = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          sep = buffer.indexOf("\n\n");
          for (const line of block.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const raw = line.slice(5).trim();
            if (!raw) continue;
            let payload: { delta?: string; tool?: string; done?: boolean; error?: string };
            try {
              payload = JSON.parse(raw);
            } catch {
              continue;
            }
            if (payload.error) {
              streamErr = payload.error;
              continue;
            }
            if (payload.delta) {
              const delta = payload.delta;
              // Once content starts streaming, the tool work is done.
              setActiveTool(null);
              setMessages((cur) =>
                cur.map((m) =>
                  m.id === replyId
                    ? { ...m, content: m.content + delta }
                    : m,
                ),
              );
            }
            if (payload.tool) {
              setActiveTool(payload.tool);
            }
            if (payload.done) {
              setActiveTool(null);
            }
          }
        }
      }

      if (streamErr) {
        setErr(streamErr);
        // Strip the empty bubble if we got nothing useful.
        setMessages((cur) =>
          cur
            .map((m) => m)
            .filter((m) => !(m.id === replyId && m.content === "")),
        );
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setMessages((cur) => cur.filter((m) => !(m.id === replyId && m.content === "")));
    } finally {
      setBusy(false);
      setActiveTool(null);
    }
  }

  const canSend = draft.trim().length > 0 && !busy;
  // The chat pane is gated on the CALL being attached, not on a known
  // customer — for unknown callers (no subscriber match), the agent
  // still wants to ask the LLM things like "what should I ask to
  // identify them?". The bot's Co-Pilot tool surface degrades
  // gracefully without customer context.
  const hasCall = stickyCallId !== undefined;
  const placeholder = !hasCall
    ? "Connect a call to enable chat"
    : stickyCustomerId
      ? "Ask anything — e.g. 'have we called them before?'"
      : "Ask anything (no subscriber matched yet)";

  return (
    <>
      <div ref={bodyRef} id="agent-desk-chat-body" className="agent-desk-chat-body">
        {messages.length === 0 && !busy && !err && (
          <div className="agent-desk-chat-placeholder">
            {!hasCall
              ? "Chat is available once a call is connected. Customer context drives the answers."
              : stickyCustomerId
                ? "Ask about the customer's history, plan, or what to try next."
                : "Caller isn't a known subscriber yet — chat still works, just without per-customer context."}
          </div>
        )}
        {messages.map((m) => {
          // The streaming bubble starts empty and grows. Show a hint
          // until the first delta lands so the user sees the request
          // was accepted.
          const pending = m.role === "copilot" && busy && m.content === "";
          return (
            <div
              key={m.id}
              id={`agent-desk-chat-msg-${m.id}`}
              className={`agent-desk-chat-msg agent-desk-chat-msg-${m.role} ${
                pending ? "is-pending" : ""
              }`}
            >
              <div className="agent-desk-chat-msg-role">
                {m.role === "agent" ? "You" : m.role === "copilot" ? "Co-Pilot" : "System"}
              </div>
              <div className="agent-desk-chat-msg-bubble">
                {m.content
                  ? m.role === "copilot"
                    ? renderMarkdown(m.content)
                    : m.content
                  : pending
                    ? "Thinking…"
                    : ""}
              </div>
            </div>
          );
        })}
        {activeTool && (
          <div
            id="agent-desk-chat-tool-pill"
            className="agent-desk-chat-tool-pill"
            role="status"
            aria-live="polite"
          >
            <span className="agent-desk-chat-tool-spinner" aria-hidden />
            {toolLabel(activeTool)}…
          </div>
        )}
        {err && (
          <div id="agent-desk-chat-err" className="dial-err">
            {err}
          </div>
        )}
      </div>

      <form
        id="agent-desk-chat-form"
        className="agent-desk-chat-input-wrap"
        onSubmit={send}
      >
        <input
          id="agent-desk-chat-input"
          className="agent-desk-chat-input"
          type="text"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={!hasCall || busy}
        />
        <button
          id="agent-desk-chat-send-btn"
          type="submit"
          className="agent-desk-chat-send"
          disabled={!canSend || !hasCall}
        >
          {busy ? "…" : "Send"}
        </button>
      </form>
    </>
  );
}
